import React, { useState } from "react";
import { Table, Button, Modal, Form } from "react-bootstrap";
import { PDFDocument, rgb, StandardFonts } from "pdf-lib";
import { saveAs } from "file-saver";
import ToPeso from "../components/misc/ToPeso";
import { format, addMonths, addWeeks, addYears } from "date-fns";

const roundUp = (amount) => {
  const amountString = amount.toFixed(2);
  const secondDecimalPlace = amountString.charAt(amountString.length - 1);
  if (secondDecimalPlace !== "0") {
    return parseFloat((amount + 0.01).toFixed(2));
  }
  return amount;
};

const calculateDueDates = (startDate, interval, term, amount, interest) => {
  const dueDates = [];
  const principalAmount = amount / term;
  const interestAmountInitial = (amount * interest) / 100;
  const interestAmountMinusAdvance = (((amount * interest) / 100) * term) - interestAmountInitial;
  const interestAmount = interestAmountMinusAdvance / term;
  let currentDate = new Date(startDate);
  for (let i = 0; i < term; i++) {
    switch (interval.toLowerCase()) {
      case "monthly":
        currentDate = addMonths(currentDate, 1);
        break;
      case "bimonthly":
        currentDate = addWeeks(currentDate, 2);
        break;
      case "weekly":
        currentDate = addWeeks(currentDate, 1);
        break;
      case "quarterly":
        currentDate = addMonths(currentDate, 3);
        break;
      case "semiannual":
        currentDate = addMonths(currentDate, 6);
        break;
      case "yearly":
        currentDate = addYears(currentDate, 1);
        break;
      default:
        throw new Error("Invalid interval");
    }
    dueDates.push({
      amortizationNumber: i + 1,
      amortizationDate: format(currentDate, "MM/dd/yyyy"),
      principal: roundUp(principalAmount),
      interest: roundUp(interestAmount),
      total: roundUp(principalAmount + interestAmount),
    });
  }
  return dueDates;
};

function DocumentPastdueAccountSecondNotice({ applicantDetails, loanProcessingDetails }) {
  const [modalShow, setModalShow] = useState(false);
  const [selectedLoan, setSelectedLoan] = useState(null);
  const [formData, setFormData] = useState({
    principalBalance: "",
    interestBalance: "",
    penaltyBalance: "",
    pdiBalance: "",
    otherChargesBalance: "",
  });

  if (!applicantDetails) {
    return null;
  }

  const handleModalOpen = (loan) => {
    setSelectedLoan(loan);
    setModalShow(true);
  };

  const handleModalClose = () => {
    setModalShow(false);
    setSelectedLoan(null);
    setFormData({
      principalBalance: "",
      interestBalance: "",
      penaltyBalance: "",
      pdiBalance: "",
      otherChargesBalance: "",
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async () => {
    // Ensure all fields are filled
    if (Object.values(formData).some((value) => !value)) {
      alert("All fields are required.");
      return;
    }

    if (!selectedLoan) return;

    const schedule = calculateDueDates(
      selectedLoan.dateGranted,
      selectedLoan.loanInterval,
      selectedLoan.loanTerm,
      selectedLoan.amountGranted,
      selectedLoan.interestRate
    );

    const existingPdfBytes = await fetch("/pdftemplates/pastdue-account-second-notice.pdf").then(res =>
      res.arrayBuffer()
    );

    const pdfDoc = await PDFDocument.load(existingPdfBytes);
    const helveticaFont = await pdfDoc.embedFont(StandardFonts.Helvetica);
    const helveticaFontBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

    const pages = pdfDoc.getPages();
    const firstPage = pages[0];

    // borrower name
    firstPage.drawText(`${applicantDetails.firstName.toUpperCase()} ${applicantDetails.middleName.toUpperCase() || ""} ${applicantDetails.lastName.toUpperCase()}`, {
      x: 37,
      y: 640,
      size: 9,
      font: helveticaFontBold, 
      color: rgb(0, 0, 0)
    });

    firstPage.drawText(`${applicantDetails.street.toUpperCase()}, ${applicantDetails.barangay.toUpperCase()},`, {
        x: 37,
        y: 628,
        size: 7,
        font: helveticaFont,
        color: rgb(0, 0, 0),
    });

    firstPage.drawText(`${applicantDetails.municipality.toUpperCase()}, ${applicantDetails.province.toUpperCase()} ${applicantDetails.zipCode.toUpperCase()}`, {
        x: 37,
        y: 619,
        size: 7,
        font: helveticaFont,
        color: rgb(0, 0, 0),
    });

    //dates

    // Get today's date
    const today = new Date();

    // Extract day, month, and year
    const day = today.getDate();
    const month = today.toLocaleString('default', { month: 'long' }); // e.g., "January", "February"
    const year = today.getFullYear();
    const dayName = today.toLocaleDateString('en-US', { weekday: 'long' });

    firstPage.drawText(`${dayName}, ${month} ${day}, ${year}`, {
      x: 37,
      y: 665,
      size: 9,
      font: helveticaFont, 
      color: rgb(0, 0, 0)
    });

    const lastSchedule = schedule[schedule.length - 1].amortizationDate;

    firstPage.drawText(`${selectedLoan.amountGranted.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`, { x: 120, y: 455, size: 9, font: helveticaFont, color: rgb(0, 0, 0) });
    firstPage.drawText(`${format(new Date(selectedLoan.dateGranted), "MM/dd/yyyy")}`, { x: 120, y: 442, size: 9, font: helveticaFont, color: rgb(0, 0, 0) });
    firstPage.drawText(`${format(new Date(lastSchedule), "MM/dd/yyyy")}`, { x: 120, y: 430, size: 9, font: helveticaFont, color: rgb(0, 0, 0) });

    const totalDue = parseFloat(formData.principalBalance) + parseFloat(formData.interestBalance) + parseFloat(formData.penaltyBalance) + parseFloat(formData.pdiBalance) + parseFloat(formData.otherChargesBalance);

    firstPage.drawText(`${parseFloat(formData.principalBalance).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`, { x: 404, y: 454, size: 9, font: helveticaFontBold, color: rgb(0, 0, 0) });
    firstPage.drawText(`${parseFloat(formData.interestBalance).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`, { x: 404, y: 442, size: 9, font: helveticaFontBold, color: rgb(0, 0, 0) });
    firstPage.drawText(`${parseFloat(formData.penaltyBalance).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`, { x: 404, y: 430, size: 9, font: helveticaFontBold, color: rgb(0, 0, 0) });
    firstPage.drawText(`${parseFloat(formData.pdiBalance).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`, { x: 404, y: 418, size: 9, font: helveticaFontBold, color: rgb(0, 0, 0) });
    firstPage.drawText(`${parseFloat(formData.otherChargesBalance).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`, { x: 404, y: 406, size: 9, font: helveticaFontBold, color: rgb(0, 0, 0) });
    firstPage.drawText(`${totalDue.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`, { x: 404, y: 367, size: 9, font: helveticaFontBold, color: rgb(0, 0, 0) });
    firstPage.drawText(`${totalDue.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`, { x: 350, y: 531, size: 9, font: helveticaFontBold, color: rgb(0, 0, 0) });

    const pdfBytes = await pdfDoc.save();
    saveAs(new Blob([pdfBytes]), `Pastdue_Account_Second_Notice_${applicantDetails.firstName}_${applicantDetails.lastName}_${selectedLoan.id}.pdf`);
    handleModalClose();
  };

  return (
    <>
      <h3>Applicant Details</h3>
      <Table className="list-container" hover responsive>
        <tbody>
          <tr>
            <td><strong>Full Name</strong></td>
            <td>{`${applicantDetails.firstName} ${applicantDetails.middleName || ""} ${applicantDetails.lastName}`}</td>
          </tr>
          <tr>
            <td><strong>Address</strong></td>
            <td>{`${applicantDetails.street}, ${applicantDetails.barangay}, ${applicantDetails.municipality}, ${applicantDetails.province}`}</td>
          </tr>
        </tbody>
      </Table>

      <h3>Loan Processing Details</h3>
      {loanProcessingDetails.length > 0 ? (
        <Table className="list-container" hover responsive>
          <thead>
            <tr>
              <th>Type</th>
              <th>Amount Granted</th>
              <th>Date Granted</th>
              <th>Interest Rate</th>
              <th>Loan Term</th>
              <th>Payment Type</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loanProcessingDetails.map((loan, index) => (
              <tr key={index}>
                <td>{loan.loanProcessingType}</td>
                <td><ToPeso amount={loan.amountGranted.toFixed(2)} /></td>
                <td>{format(new Date(loan.dateGranted), "MM/dd/yyyy")}</td>
                <td>{loan.interestRate}%</td>
                <td>{loan.loanTerm} months</td>
                <td>{loan.paymentType}</td>
                <td>
                  <Button variant="primary" onClick={() => handleModalOpen(loan)}>
                    Generate
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      ) : (
        <p>No loan processing details found for this applicant.</p>
      )}

      <Modal show={modalShow} onHide={handleModalClose}>
        <Modal.Header closeButton>
          <Modal.Title>Additional Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group>
              <Form.Label>Principal Balance</Form.Label>
              <Form.Control type="number" name="principalBalance" value={formData.principalBalance} onChange={handleInputChange} required />
            </Form.Group>
            <Form.Group>
              <Form.Label>Interest Balance</Form.Label>
              <Form.Control type="number" name="interestBalance" value={formData.interestBalance} onChange={handleInputChange} required />
            </Form.Group>
            <Form.Group>
              <Form.Label>Penalty Balance</Form.Label>
              <Form.Control type="number" name="penaltyBalance" value={formData.penaltyBalance} onChange={handleInputChange} required />
            </Form.Group>
            <Form.Group>
              <Form.Label>PDI Balance</Form.Label>
              <Form.Control type="number" name="pdiBalance" value={formData.pdiBalance} onChange={handleInputChange} required />
            </Form.Group>
            <Form.Group>
              <Form.Label>Other Charges Balance</Form.Label>
              <Form.Control type="number" name="otherChargesBalance" value={formData.otherChargesBalance} onChange={handleInputChange} required />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleModalClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSubmit}>
            Submit
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default DocumentPastdueAccountSecondNotice;
